// src/pages/Dashboard.jsx - VISUAL REDESIGN ONLY (All functionality preserved)
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Book, 
  Edit, 
  Settings, 
  Eye, 
  Lock, 
  Globe, 
  Copy,
  Download,
  Upload,
  BarChart3,
  Sparkles,
  Search
} from 'lucide-react';
import { firestoreService } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { bookAccessService } from '../services/bookAccessService';
import { csvService } from '../services/csvService';

export const Dashboard = () => {
  const { user, userProfile } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');


  useEffect(() => {
    if (user) {
      loadUserBooks();
    }
  }, [user]);

  const loadUserBooks = async () => {
    try {
      const userBooks = await bookAccessService.getUserAccessibleBooks(user.uid);
      setBooks(userBooks);
    } catch (error) {
      console.error('Error loading books:', error);
    } finally {
      setLoading(false);
    }
  }

  const filteredBooks = books
  .filter(book => {
    switch (filter) {
      case 'published':
        return book.published;
      case 'draft':
        return !book.published;
      case 'public':
        return book.accessLevel === 'public';
      case 'private':
        return book.accessLevel === 'private';
      default:
        return true;
    }
  })
  .filter(book =>
    book.botName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (book.botCategory || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (book.botTask || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: books.length,
    published: books.filter(b => b.published).length,
    views: books.reduce((sum, b) => sum + (b.viewCount || 0), 0),
    public: books.filter(b => b.accessLevel === 'public').length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header - ENHANCED VISUALS ONLY */}
        <div className="mb-8 bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 rounded-3xl p-8 text-white shadow-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full mb-4">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">Your Creative Workspace</span>
              </div>
              <h1 className="text-4xl font-bold mb-2">
                Welcome back, {userProfile?.displayName || user?.displayName || 'User'}!
              </h1>
              <p className="text-lg text-white/90">
                Manage your interactive pages and track their performance
              </p>
            </div>
            
            <Link
              to="/create"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-purple-600 rounded-xl font-bold hover:scale-105 transition-all duration-200"
              style={{
              boxShadow: `
                inset 0 2px 3px rgba(0, 0, 0, 0.4),
                0 0 8px rgba(255, 255, 255, 0.4)
              `,
            }}
            >
              <Plus className="h-5 w-5" />
              Create New Page
            </Link>
          </div>
        </div>

        {/* Stats Grid - ENHANCED VISUALS ONLY */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard 
            icon={<Book className="h-6 w-6" />} 
            title="Total Pages" 
            value={stats.total} 
            color="blue" 
          />
          <StatsCard 
            icon={<Globe className="h-6 w-6" />} 
            title="Published" 
            value={stats.published} 
            color="green" 
          />
          <StatsCard 
            icon={<BarChart3 className="h-6 w-6" />} 
            title="Total Views" 
            value={stats.views} 
            color="purple" 
          />
          <StatsCard 
            icon={<Globe className="h-6 w-6" />} 
            title="Public Books" 
            value={stats.public} 
            color="orange" 
          />
        </div>

        {/* Filter Section - ENHANCED VISUALS ONLY */}
        <div className="mb-6 bg-white rounded-2xl shadow-lg p-6">

          {/* 🔍 Search Bar */}
          <div className="flex items-center gap-3 mb-4 bg-slate-100 px-4 py-3 rounded-xl border border-slate-200 shadow-sm">
            <Search className="text-slate-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search books..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent focus:outline-none border border-gray-50 rounded-xl text-slate-700 placeholder-slate-400"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {['all', 'published', 'draft', 'public', 'private'].map((filterOption) => (
              <button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                  filter === filterOption
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg scale-105'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:scale-102'
                }`}
              >
                {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Books Grid - NO CHANGES TO FUNCTIONALITY */}
        {filteredBooks.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBooks.map((book) => (
              <BookDashboardCard
                user={user}
                key={book.id}
                book={book}
                onUpdate={loadUserBooks}
              />
            ))}
          </div>
        ) : (
          <EmptyState filter={filter} />
        )}
      </div>
    </div>
  );
};

// Stats Card Component - ENHANCED VISUALS ONLY
const StatsCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-cyan-500 bg-gradient-to-br from-blue-50 to-cyan-50',
    green: 'from-green-500 to-emerald-500 bg-gradient-to-br from-green-50 to-emerald-50',
    purple: 'from-purple-500 to-pink-500 bg-gradient-to-br from-purple-50 to-pink-50',
    orange: 'from-orange-500 to-red-500 bg-gradient-to-br from-orange-50 to-red-50'
  };

  const gradients = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-red-500'
  };

  const backgrounds = {
    blue: 'from-blue-50 to-cyan-50',
    green: 'from-green-50 to-emerald-50',
    purple: 'from-purple-50 to-pink-50',
    orange: 'from-orange-50 to-red-50'
  };

  return (
    <div className={`bg-gradient-to-br ${backgrounds[color]} rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1`}>
      <div className="flex items-center">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${gradients[color]} transform hover:scale-110 hover:rotate-6 transition-all duration-300`}>
          <div className="text-white">{icon}</div>
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};


const BookDashboardCard = ({ book, user, onUpdate }) => {
  const [exporting, setExporting] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const exportBook = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setExporting(true);
    try {
      const csvContent = await csvService.exportBookToCSV(book.id);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${book.botName}_export.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDuplicate = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm(`Duplicate "${book.botName}"?`)) return;
    
    setDuplicating(true);
    try {
      await firestoreService.duplicateBook(book.id, user.uid);
      onUpdate();
    } catch (error) {
      console.error('Duplicate failed:', error);
      alert('Failed to duplicate book');
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
      <div className="h-40 bg-gradient-to-br from-purple-200 via-blue-100 to-pink-200 relative overflow-hidden">
        {book.ogImage ? (
          <img src={book.ogImage} alt={book.botName} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <Book className="w-16 h-16 text-white/30" />
          </div>
        )}
        <div className="absolute top-4 left-4 flex gap-2">
          {book.published && (
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
              Published
            </span>
          )}
          {book.accessLevel === 'public' && (
            <span className="px-3 py-1 bg-blue-500 text-white text-xs font-semibold rounded-full">
              Public
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-1 hover:text-purple-600 transition-colors">
              {book.botName}
            </h3>
            <p className="text-sm text-gray-600">{book.botCategory}</p>
          </div>
          
          <div className="flex gap-1">
            {book.accessLevel === 'public' ? (
              <Globe className="h-4 w-4 text-green-500" title="Public" />
            ) : (
              <Lock className="h-4 w-4 text-gray-400" title="Private" />
            )}
            {book.published && (
              <div className="w-2 h-2 bg-green-500 rounded-full" title="Published" />
            )}
          </div>
        </div>

        <p className="text-gray-700 text-sm mb-4 line-clamp-2">
          {book.botTask || 'Interactive digital book'}
        </p>

        <div className="flex items-center justify-between text-xs text-gray-500 mb-4 pb-4 border-b border-gray-100">
          <span>Views: {book.viewCount || 0}</span>
          <span>{book.published ? 'Published' : 'Draft'}</span>
        </div>

        <div className="flex gap-2">
          <Link
            to={`/books/${book.id}`}
            className="flex-1 bg-blue-50 text-blue-600 px-4 py-3 rounded-lg text-sm font-medium hover:bg-blue-100 text-center transition-all hover:scale-105"
          >
            <Eye className="h-4 w-4 inline mr-1" />
            View
          </Link>
          
          <Link
            to={`/books/${book.id}/edit`}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:shadow-lg text-center transition-all hover:scale-105"
          >
            <Edit className="h-4 w-4 inline mr-1" />
            Edit
          </Link>
          
          <button
            onClick={handleDuplicate}
            disabled={duplicating}
            className="bg-purple-50 text-purple-600 px-4 py-3 rounded-lg text-sm font-medium hover:bg-purple-100 disabled:opacity-50 transition-all hover:scale-105"
            title="Duplicate Book"
          >
            <Copy className="h-4 w-4" />
          </button>

          <button
            onClick={exportBook}
            disabled={exporting}
            className="bg-green-50 text-green-600 px-4 py-3 rounded-lg text-sm font-medium hover:bg-green-100 disabled:opacity-50 transition-all hover:scale-105"
            title="Export CSV"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Empty State Component - ENHANCED VISUALS ONLY
const EmptyState = ({ filter }) => {
  const getEmptyMessage = () => {
    switch (filter) {
      case 'published':
        return 'No published books yet';
      case 'draft':
        return 'No draft books';
      case 'public':
        return 'No public books';
      case 'private':
        return 'No private books';
      default:
        return 'No books created yet';
    }
  };

  return (
    <div className="text-center py-16 px-4">
      <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 rounded-full mb-6">
        <Book className="h-12 w-12 text-purple-600" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-3">
        {getEmptyMessage()}
      </h3>
      <p className="text-lg text-gray-600 mb-8">
        {filter === 'all' 
          ? "Start by creating your first interactive book"
          : `You don't have any ${filter} books yet`
        }
      </p>
      {filter === 'all' && (
        <Link
          to="/create"
          className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200"
        >
          <Plus className="h-5 w-5" />
          Create Your First Book
        </Link>
      )}
    </div>
  );
};